import type { IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';

/**
 * Structured address picked from the Places typeahead (or hydrated from an
 * existing `Residence`), flattened to the `upsertResidence` columns.
 * `addressLine1` is `null` for non-street picks (a city/region like
 * "Truckee, CA") — primary residences gate on it being present; frequent
 * locations do not. Shared by the onboarding residence step and the dependents
 * settings form.
 */
export interface IResidenceAddress {
  formatted: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  country: string;
  placeId: string | null;
}

/** Map a Google Places result into the flat residence-address shape. */
export const toResidenceAddress = (p: IParsedAddress): IResidenceAddress => ({
  formatted: p.formattedAddress,
  addressLine1: p.streetNumber && p.streetName ? `${p.streetNumber} ${p.streetName}` : null,
  addressLine2: p.subpremise ?? null,
  city: p.city ?? '',
  state: p.state ?? null,
  postalCode: p.postalCode ?? null,
  country: p.country ?? '',
  placeId: p.placeId || null,
});

/**
 * A picked address is upsertable only when both NOT-NULL residence columns
 * (city + country) are present. Google returns plenty of legitimate picks (a
 * region, a POI, a non-locality place) with no city component, which
 * `toResidenceAddress` flattens to `''` — that empty value would fail the
 * backend's non-empty-text assertion, so callers gate on this before saving.
 */
export const isCompleteAddress = (address: IResidenceAddress | null): address is IResidenceAddress =>
  address !== null && address.city.trim().length > 0 && address.country.trim().length > 0;
